import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar, Mail, Settings, LogOut, Clock, Image, DollarSign,
  Trash2, Check, X, ChevronDown, Plus, CreditCard, Link as LinkIcon, Users, Key,
  FileText, Tag, Wrench, Camera, MessageSquare, Send, Pen,
  FolderOpen, Upload, Eye, Download, RefreshCw, BookOpen, PenTool, Repeat, UserCog, History, Layout, Briefcase, CalendarDays, ClipboardList
} from 'lucide-react';
import axios from 'axios';
import { CalendarTab } from '../components/admin/CalendarTab';
import { VisitsModal } from '../components/admin/VisitsModal';
import { WebsiteEditorTab } from '../components/admin/WebsiteEditorTab';
import { MediaLibraryTab } from '../components/admin/MediaLibraryTab';
import { TeamModal, WarrantyRegistrationsBlock } from '../components/admin/TeamAndWarranty';
import { useTheme, ThemeToggle } from '../components/ThemeToggle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: { bg: 'bg-yellow-500/10', text: 'text-yellow-500' },
  confirmed: { bg: 'bg-green-500/10', text: 'text-green-500' },
  completed: { bg: 'bg-blue-500/10', text: 'text-blue-500' },
  cancelled: { bg: 'bg-red-500/10', text: 'text-red-400' },
};

const maintenanceStatusColors = {
  scheduled: { bg: 'bg-indigo-500/10', text: 'text-indigo-400' },
  in_progress: { bg: 'bg-yellow-500/10', text: 'text-yellow-500' },
  completed: { bg: 'bg-green-500/10', text: 'text-green-500' },
  cancelled: { bg: 'bg-red-500/10', text: 'text-red-400' },
};

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [payments, setPayments] = useState([]);
  const [clients, setClients] = useState([]);
  const [uanCodes, setUanCodes] = useState([]);
  const [uanQty, setUanQty] = useState(1);
  const [generatedCodes, setGeneratedCodes] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [projectPhotos, setProjectPhotos] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [visitsSub, setVisitsSub] = useState(null);
  const [teamClient, setTeamClient] = useState(null);
  const [theme, toggleTheme] = useTheme('drt-admin-theme');
  const [workspace, setWorkspace] = useState('crm');
  const [staff, setStaff] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [servicePlans, setServicePlans] = useState([]);
  const [clientDetail, setClientDetail] = useState(null);
  const [planEdit, setPlanEdit] = useState(null);
  const [staffForm, setStaffForm] = useState({ name: '', email: '', password: '', title: '', role: 'customer_service' });
  const [conversations, setConversations] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [convoMessages, setConvoMessages] = useState([]);
  const [adminNewMsg, setAdminNewMsg] = useState('');
  const [activeTab, setActiveTab] = useState('bookings');
  const [showAddGallery, setShowAddGallery] = useState(false);
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [showAddPromo, setShowAddPromo] = useState(false);
  const [showAddMaint, setShowAddMaint] = useState(false);
  const [showAddPhoto, setShowAddPhoto] = useState(false);
  const [newGalleryItem, setNewGalleryItem] = useState({ title: '', description: '', category: 'theater-rooms', image_url: '', order: 0 });
  const [newDoc, setNewDoc] = useState({ client_id: '', title: '', doc_type: 'contract', file_url: '', notes: '', signature_required: false, folder: 'general' });
  const [newPromo, setNewPromo] = useState({ title: '', description: '', discount_type: 'percentage', discount_value: 0, promo_code: '', valid_until: '', is_active: true });
  const [newMaint, setNewMaint] = useState({ client_id: '', service_type: 'routine', description: '', scheduled_date: '', technician: '', notes: '' });
  const [newPhoto, setNewPhoto] = useState({ client_id: '', title: '', description: '', image_url: '', phase: 'completed' });
  const navigate = useNavigate();
  const adminName = localStorage.getItem('admin_name') || 'Admin';
  const token = localStorage.getItem('admin_token');

  const headers = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    if (!token) { navigate('/admin'); return; }
    try {
      const [statsRes, bookingsRes, contactsRes, galleryRes, paymentsRes, clientsRes, uanRes, docsRes, promosRes, maintRes, photosRes, convosRes, subsRes, staffRes, auditRes, plansRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/bookings`, { headers }),
        axios.get(`${API}/admin/contacts`, { headers }),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/admin/payments`, { headers }),
        axios.get(`${API}/admin/clients`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/uan/list`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/documents`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/promotions`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/maintenance`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/project-photos`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/conversations`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/subscriptions`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/staff`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/audit-log`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/service-plans`, { headers }).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setContacts(contactsRes.data);
      setGallery(galleryRes.data);
      setPayments(paymentsRes.data);
      setClients(clientsRes.data);
      setUanCodes(uanRes.data);
      setDocuments(docsRes.data);
      setPromotions(promosRes.data);
      setMaintenance(maintRes.data);
      setProjectPhotos(photosRes.data);
      setConversations(convosRes.data);
      setSubscriptions(subsRes.data);
      setStaff(staffRes.data);
      setAuditLog(auditRes.data);
      setServicePlans(plansRes.data);
    } catch (err) {
      if (err.response?.status === 401) navigate('/admin');
    }
  }, [token, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_name');
    navigate('/admin');
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}`, { status }, { headers });
      loadData();
    } catch (err) { console.error(err); }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Delete this booking?')) return;
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`, { headers });
      loadData();
    } catch (err) { console.error(err); }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await axios.delete(`${API}/admin/contacts/${contactId}`, { headers });
      loadData();
    } catch (err) { console.error(err); }
  };

  const sendSubscriptionLink = async (subId) => {
    if (!window.confirm('Send a fresh Square payment link to this client now?')) return;
    try {
      await axios.post(`${API}/admin/subscriptions/${subId}/send-link`, {}, { headers });
      alert('Payment link emailed to client.');
      loadData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to send link'); }
  };

  const toggleSubscription = async (sub) => {
    const next = sub.status === 'active' ? 'cancelled' : 'active';
    if (!window.confirm(`Set this subscription to ${next}?`)) return;
    try {
      await axios.patch(`${API}/admin/subscriptions/${sub.id}`, { status: next }, { headers });
      loadData();
    } catch (err) { console.error(err); }
  };

  const setClientTier = async (client, tierVal) => {
    if (!window.confirm(`Set ${client.name || client.email} to Tier ${tierVal} (${tierVal === 2 ? 'Full Project Access' : 'Standard Access'})?`)) return;
    try {
      await axios.patch(`${API}/admin/clients/${client.id}/access`, { tier: tierVal }, { headers });
      loadData();
      if (clientDetail?.client?.id === client.id) openClientDetail(client.id);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to update access'); }
  };

  const openClientDetail = async (clientId) => {
    try {
      const res = await axios.get(`${API}/admin/clients/${clientId}/full`, { headers });
      setClientDetail(res.data);
    } catch (err) { alert('Failed to load client record'); }
  };

  const createStaff = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/staff`, staffForm, { headers });
      setStaffForm({ name: '', email: '', password: '', title: '', role: 'customer_service' });
      loadData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to create staff'); }
  };

  const updateStaffField = async (staffId, field, value) => {
    try {
      await axios.patch(`${API}/admin/staff/${staffId}`, { [field]: value }, { headers });
      loadData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to update staff'); }
  };

  const savePlan = async (e) => {
    e.preventDefault();
    const body = {
      ...planEdit,
      monthly: parseFloat(planEdit.monthly) || 0,
      annual_full: parseFloat(planEdit.annual_full) || 0,
      annual: parseFloat(planEdit.annual) || 0,
      per_visit_price: planEdit.per_visit_price ? parseFloat(planEdit.per_visit_price) : null,
      visits_per_year: parseInt(planEdit.visits_per_year) || 0,
      features: typeof planEdit.features === 'string' ? planEdit.features.split('\n').filter(f => f.trim()) : planEdit.features,
    };
    try {
      if (planEdit.id) {
        const { id, created_at, updated_at, currency, annual_discount_pct, ...updates } = body;
        await axios.patch(`${API}/admin/service-plans/${planEdit.id}`, updates, { headers });
      } else {
        await axios.post(`${API}/admin/service-plans`, body, { headers });
      }
      setPlanEdit(null);
      loadData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save plan'); }
  };

  const addGalleryItem = async () => {
    try {
      await axios.post(`${API}/admin/gallery`, newGalleryItem, { headers });
      setShowAddGallery(false);
      setNewGalleryItem({ title: '', description: '', category: 'theater-rooms', image_url: '', order: 0 });
      loadData();
    } catch (err) { console.error(err); }
  };

  const generateUAN = async () => {
    try {
      const res = await axios.post(`${API}/admin/uan/generate`, { count: uanQty }, { headers });
      setGeneratedCodes(res.data.codes);
      loadData();
    } catch (err) { console.error(err); }
  };

  const deleteGalleryItem = async (itemId) => {
    if (!window.confirm('Delete this gallery item?')) return;
    try {
      await axios.delete(`${API}/admin/gallery/${itemId}`, { headers });
      loadData();
    } catch (err) { console.error(err); }
  };

  // Document actions
  const addDocument = async () => {
    try {
      await axios.post(`${API}/admin/documents`, newDoc, { headers });
      setShowAddDoc(false);
      setNewDoc({ client_id: '', title: '', doc_type: 'contract', file_url: '', notes: '', signature_required: false, folder: 'general' });
      loadData();
    } catch (err) { console.error(err); }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/admin/documents/${docId}`, { headers });
      loadData();
    } catch (err) { console.error(err); }
  };

  // Promotion actions
  const addPromotion = async () => {
    try {
      await axios.post(`${API}/admin/promotions`, newPromo, { headers });
      setShowAddPromo(false);
      setNewPromo({ title: '', description: '', discount_type: 'percentage', discount_value: 0, promo_code: '', valid_until: '', is_active: true });
      loadData();
    } catch (err) { console.error(err); }
  };

  const togglePromotion = async (promoId, isActive) => {
    try {
      await axios.patch(`${API}/admin/promotions/${promoId}`, { is_active: !isActive }, { headers });
      loadData();
    } catch (err) { console.error(err); }
  };

  const deletePromotion = async (promoId) => {
    if (!window.confirm('Delete this promotion?')) return;
    try {
      await axios.delete(`${API}/admin/promotions/${promoId}`, { headers });
      loadData();
    } catch (err) { console.error(err); }
  };

  // Maintenance actions
  const addMaintenance = async () => {
    try {
      await axios.post(`${API}/admin/maintenance`, newMaint, { headers });
      setShowAddMaint(false);
      setNewMaint({ client_id: '', service_type: 'routine', description: '', scheduled_date: '', technician: '', notes: '' });
      loadData();
    } catch (err) { console.error(err); }
  };

  const updateMaintenanceStatus = async (recordId, status) => {
    try {
      const updateData = { status };
      if (status === 'completed') updateData.completed_date = new Date().toISOString().split('T')[0];
      await axios.patch(`${API}/admin/maintenance/${recordId}`, updateData, { headers });
      loadData();
    } catch (err) { console.error(err); }
  };

  const deleteMaintenance = async (recordId) => {
    if (!window.confirm('Delete this maintenance record?')) return;
    try {
      await axios.delete(`${API}/admin/maintenance/${recordId}`, { headers });
      loadData();
    } catch (err) { console.error(err); }
  };

  // Project Photo actions
  const addProjectPhoto = async () => {
    try {
      await axios.post(`${API}/admin/project-photos`, newPhoto, { headers });
      setShowAddPhoto(false);
      setNewPhoto({ client_id: '', title: '', description: '', image_url: '', phase: 'completed' });
      loadData();
    } catch (err) { console.error(err); }
  };

  const deleteProjectPhoto = async (photoId) => {
    if (!window.confirm('Delete this project photo?')) return;
    try {
      await axios.delete(`${API}/admin/project-photos/${photoId}`, { headers });
      loadData();
    } catch (err) { console.error(err); }
  };

  // Messaging actions
  const openConversation = async (clientId) => {
    try {
      const res = await axios.get(`${API}/admin/messages/${clientId}`, { headers });
      setSelectedConvo(res.data.conversation);
      setConvoMessages(res.data.messages || []);
      loadData();
    } catch (err) { console.error(err); }
  };

  const sendAdminMessage = async () => {
    if (!adminNewMsg.trim() || !selectedConvo) return;
    try {
      await axios.post(`${API}/admin/messages`, { client_id: selectedConvo.client_id, content: adminNewMsg }, { headers });
      setAdminNewMsg('');
      openConversation(selectedConvo.client_id);
    } catch (err) { console.error(err); }
  };

  const startNewConvo = async (clientId) => {
    setSelectedConvo({ client_id: clientId });
    setConvoMessages([]);
    try {
      const res = await axios.get(`${API}/admin/messages/${clientId}`, { headers });
      if (res.data.conversation) setSelectedConvo(res.data.conversation);
      setConvoMessages(res.data.messages || []);
    } catch (e) { /* ignored */ }
  };

  const connectCalendar = async () => {
    try {
      const res = await axios.get(`${API}/admin/calendar/auth`, { headers });
      if (res.data.authorization_url) {
        window.location.href = res.data.authorization_url;
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Google Calendar not configured yet. Add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to backend .env');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  const statCards = stats ? [
    { label: 'Bookings', value: stats.total_bookings, icon: Calendar, color: '#D4AF37' },
    { label: 'Pending', value: stats.pending_bookings, icon: Clock, color: '#f59e0b' },
    { label: 'Clients', value: stats.total_clients, icon: Users, color: '#10b981' },
    { label: 'Payments', value: stats.total_payments, icon: DollarSign, color: '#ec4899' },
    { label: 'Documents', value: stats.total_documents, icon: FileText, color: '#6366f1' },
    { label: 'Maintenance', value: stats.total_maintenance, icon: Wrench, color: '#14b8a6' },
  ] : [];

  const crmTabs = [
    { id: 'bookings', label: 'Bookings', icon: Calendar },
    { id: 'calendar', label: 'Calendar', icon: CalendarDays },
    { id: 'clients', label: 'Clients / CRM', icon: Users },
    { id: 'uan', label: 'UAN / Access', icon: Key },
    { id: 'payments', label: 'Payments', icon: DollarSign },
    { id: 'subscriptions', label: 'Subscriptions', icon: Repeat },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'documents', label: 'Client Docs', icon: FileText },
    { id: 'signatures', label: 'Signatures', icon: PenTool },
    { id: 'maintenance', label: 'Maintenance', icon: Wrench },
    { id: 'photos', label: 'Project Photos', icon: Camera },
    { id: 'contacts', label: 'Contacts', icon: Mail },
    { id: 'staff', label: 'Staff', icon: UserCog },
    { id: 'audit', label: 'Audit Log', icon: History },
  ];

  const websiteTabs = [
    { id: 'editor', label: 'Website Editor', icon: Layout },
    { id: 'media', label: 'Media Library', icon: Camera },
    { id: 'gallery', label: 'Gallery', icon: Image },
    { id: 'plans', label: 'Packages & Pricing', icon: Briefcase },
    { id: 'promotions', label: 'Promos', icon: Tag },
    { id: 'library', label: 'Docs & Booklets', icon: FolderOpen },
  ];

  const tabs = workspace === 'crm' ? crmTabs : websiteTabs;

  const switchWorkspace = (ws) => {
    setWorkspace(ws);
    setActiveTab(ws === 'crm' ? 'bookings' : 'editor');
  };

  // Client select options for forms
  const clientOptions = clients.map(c => ({ id: c.id, label: `${c.name} (${c.email})` }));

  return (
    <div className={`min-h-screen ${theme === 'light' ? 'theme-light' : ''}`} data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-white/5 bg-[#0f0f10] sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 rounded-sm bg-[#D4AF37] flex items-center justify-center">
              <span className="font-playfair font-bold text-black text-sm">D</span>
            </div>
            <div>
              <h1 className="font-playfair text-lg text-white">Admin Dashboard</h1>
              <p className="text-white/30 font-manrope text-xs">Welcome, {adminName}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <ThemeToggle theme={theme} onToggle={toggleTheme} testId="admin-theme-toggle" />
            <button
              onClick={connectCalendar}
              className="hidden sm:flex items-center gap-2 text-[#D4AF37]/60 hover:text-[#D4AF37] font-manrope text-xs uppercase tracking-widest transition-colors border border-[#D4AF37]/20 px-3 py-1.5 rounded-sm"
              data-testid="connect-calendar-btn"
            >
              <LinkIcon size={12} /> Connect Calendar
            </button>
            <button onClick={logout} className="flex items-center gap-2 text-white/40 hover:text-red-400 font-manrope text-xs uppercase tracking-widest transition-colors" data-testid="admin-logout-btn">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="admin-stats">
            {statCards.map((card, i) => {
              const Icon = card.icon;
              return (
                <motion.div key={card.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="bg-[#111113] border border-white/5 rounded-sm p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={14} style={{ color: card.color }} />
                    <span className="text-white/30 font-manrope text-[10px] uppercase tracking-widest">{card.label}</span>
                  </div>
                  <span className="font-playfair text-2xl text-white">{card.value}</span>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Workspace Toggle */}
        <div className="flex items-center gap-2 mb-6" data-testid="workspace-toggle">
          <button
            onClick={() => switchWorkspace('crm')}
            className={`flex items-center gap-2.5 px-6 py-3 rounded-sm font-manrope text-xs uppercase tracking-[0.2em] font-bold transition-all duration-300 border ${workspace === 'crm' ? 'bg-[#D4AF37] text-black border-[#D4AF37]' : 'bg-transparent text-white/40 border-white/10 hover:text-white/70 hover:border-white/25'}`}
            data-testid="workspace-crm"
          >
            <Users size={15} /> CRM & Operations
          </button>
          <button
            onClick={() => switchWorkspace('website')}
            className={`flex items-center gap-2.5 px-6 py-3 rounded-sm font-manrope text-xs uppercase tracking-[0.2em] font-bold transition-all duration-300 border ${workspace === 'website' ? 'bg-[#D4AF37] text-black border-[#D4AF37]' : 'bg-transparent text-white/40 border-white/10 hover:text-white/70 hover:border-white/25'}`}
            data-testid="workspace-website"
          >
            <Layout size={15} /> Website Management
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-white/5 pb-4 overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2 rounded-sm font-manrope text-xs uppercase tracking-widest transition-all duration-300 whitespace-nowrap ${activeTab === tab.id ? 'bg-[#D4AF37] text-black font-bold' : 'text-white/40 hover:text-white/70'}`} data-testid={`tab-${tab.id}`}>
                <Icon size={14} /> {tab.label}
              </button>
            );
          })}
        </div>

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="bg-[#111113] border border-white/5 rounded-sm overflow-hidden" data-testid="bookings-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    {['Name', 'Email', 'Phone', 'Service', 'Budget', 'Date / Time', 'Status', 'Actions'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-white/30 font-manrope">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bookings.length === 0 ? (
                    <tr><td colSpan="8" className="px-4 py-8 text-center text-white/30 font-manrope text-sm">No bookings yet</td></tr>
                  ) : bookings.map(b => (
                    <tr key={b.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3 text-white/80 font-manrope text-sm">{b.full_name}</td>
                      <td className="px-4 py-3 text-white/50 font-manrope text-sm">{b.email}</td>
                      <td className="px-4 py-3 text-white/50 font-manrope text-sm">{b.phone}</td>
                      <td className="px-4 py-3 text-white/50 font-manrope text-xs uppercase">{b.service_type}</td>
                      <td className="px-4 py-3 text-white/50 font-manrope text-xs" data-testid={`booking-budget-${b.id}`}>{b.budget || '—'}</td>
                      <td className="px-4 py-3 text-white/50 font-manrope text-sm">{b.preferred_date || '—'} {b.preferred_time || ''}</td>
                      <td className="px-4 py-3">
                        <div className="relative group">
                          <button className={`${statusColors[b.status]?.bg || 'bg-white/5'} ${statusColors[b.status]?.text || 'text-white/40'} px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold flex items-center gap-1`} data-testid={`booking-status-${b.id}`}>
                            {b.status} <ChevronDown size={10} />
                          </button>
                          <div className="absolute top-full left-0 mt-1 bg-[#1a1a1a] border border-white/10 rounded-sm shadow-xl z-10 hidden group-hover:block min-w-[120px]">
                            {['pending', 'confirmed', 'completed', 'cancelled'].map(s => (
                              <button key={s} onClick={() => updateBookingStatus(b.id, s)} className="block w-full text-left px-3 py-2 text-xs font-manrope text-white/60 hover:text-[#D4AF37] hover:bg-white/5 uppercase tracking-widest" data-testid={`set-status-${s}-${b.id}`}>
                                {s}
                              </button>
                            ))}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => deleteBooking(b.id)} className="text-white/20 hover:text-red-400 transition-colors" data-testid={`delete-booking-${b.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Website Editor Tab */}
        {activeTab === 'editor' && <WebsiteEditorTab headers={headers} />}

        {/* Media Library Tab */}
        {activeTab === 'media' && <MediaLibraryTab headers={headers} />}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <CalendarTab headers={headers} clients={clients} staff={staff} />
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="bg-[#111113] border border-white/5 rounded-sm overflow-hidden" data-testid="contacts-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    {['Name', 'Email', 'Phone', 'Message', 'Submitted', 'Actions'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-white/30 font-manrope">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {contacts.length === 0 ? (
                    <tr><td colSpan="6" className="px-4 py-8 text-center text-white/30 font-manrope text-sm">No contacts yet</td></tr>
                  ) : contacts.map(c => (
                    <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3 text-white/80 font-manrope text-sm">{c.full_name}</td>
                      <td className="px-4 py-3 text-white/50 font-manrope text-sm">{c.email}</td>
                      <td className="px-4 py-3 text-white/50 font-manrope text-sm">{c.phone}</td>
                      <td className="px-4 py-3 text-white/50 font-manrope text-sm max-w-[250px] truncate">{c.message || '—'}</td>
                      <td className="px-4 py-3 text-white/30 font-manrope text-xs">{formatDate(c.created_at)}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => deleteContact(c.id)} className="text-white/20 hover:text-red-400 transition-colors" data-testid={`delete-contact-${c.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <div data-testid="gallery-management">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-manrope text-white/60 text-sm">{gallery.length} items</h3>
              <button onClick={() => setShowAddGallery(!showAddGallery)} className="flex items-center gap-2 bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest px-4 py-2 rounded-sm hover:bg-[#C5A059] transition-all" data-testid="add-gallery-btn">
                <Plus size={14} /> Add Item
              </button>
            </div>

            {showAddGallery && (
              <div className="bg-[#111113] border border-[#D4AF37]/20 rounded-sm p-6 mb-6 space-y-4" data-testid="add-gallery-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input value={newGalleryItem.title} onChange={e => setNewGalleryItem(p => ({ ...p, title: e.target.value }))} placeholder="Title" className="bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white placeholder:text-white/20 font-manrope text-sm" data-testid="gallery-title-input" />
                  <select value={newGalleryItem.category} onChange={e => setNewGalleryItem(p => ({ ...p, category: e.target.value }))} className="bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white font-manrope text-sm" data-testid="gallery-category-select">
                    <option value="audio-video" className="bg-[#111]">A/V Audio Video</option>
                    <option value="theater-rooms" className="bg-[#111]">Theater Rooms</option>
                    <option value="specialty-items" className="bg-[#111]">Specialty Items</option>
                    <option value="pre-wire" className="bg-[#111]">Pre-Wire</option>
                    <option value="customizations" className="bg-[#111]">Customizations</option>
                  </select>
                </div>
                <input value={newGalleryItem.image_url} onChange={e => setNewGalleryItem(p => ({ ...p, image_url: e.target.value }))} placeholder="Image URL" className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white placeholder:text-white/20 font-manrope text-sm" data-testid="gallery-url-input" />
                <input value={newGalleryItem.description} onChange={e => setNewGalleryItem(p => ({ ...p, description: e.target.value }))} placeholder="Description" className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white placeholder:text-white/20 font-manrope text-sm" data-testid="gallery-desc-input" />
                <div className="flex gap-3">
                  <button onClick={addGalleryItem} className="bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest px-6 py-2 rounded-sm hover:bg-[#C5A059] transition-all" data-testid="save-gallery-btn">Save</button>
                  <button onClick={() => setShowAddGallery(false)} className="text-white/40 font-manrope text-xs uppercase tracking-widest px-6 py-2 border border-white/10 rounded-sm hover:border-white/30 transition-all">Cancel</button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {gallery.map(item => (
                <div key={item.id} className="bg-[#111113] border border-white/5 rounded-sm overflow-hidden group relative" data-testid={`gallery-admin-${item.id}`}>
                  <div className="aspect-square">
                    <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-3">
                    <p className="text-white/80 font-manrope text-xs font-semibold truncate">{item.title}</p>
                    <p className="text-white/30 font-manrope text-[10px] uppercase tracking-widest">{item.category}</p>
                  </div>
                  <button onClick={() => deleteGalleryItem(item.id)} className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm text-white/50 hover:text-red-400 w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all" data-testid={`delete-gallery-${item.id}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="bg-[#111113] border border-white/5 rounded-sm overflow-hidden" data-testid="payments-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    {['Package', 'Amount', 'Currency', 'Status', 'Session ID', 'Created'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-white/30 font-manrope">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 ? (
                    <tr><td colSpan="6" className="px-4 py-8 text-center text-white/30 font-manrope text-sm">No payments yet</td></tr>
                  ) : payments.map(p => (
                    <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3 text-white/80 font-manrope text-sm">{p.package_name}</td>
                      <td className="px-4 py-3 text-[#D4AF37] font-manrope font-semibold">${p.amount}</td>
                      <td className="px-4 py-3 text-white/50 font-manrope text-xs uppercase">{p.currency}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold ${p.payment_status === 'paid' ? 'bg-green-500/10 text-green-500' : p.payment_status === 'pending' ? 'bg-yellow-500/10 text-yellow-500' : 'bg-white/5 text-white/40'}`}>
                          {p.payment_status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white/30 font-manrope text-xs font-mono">{p.session_id?.slice(0, 20)}...</td>
                      <td className="px-4 py-3 text-white/30 font-manrope text-xs">{formatDate(p.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Subscriptions Tab */}
        {activeTab === 'subscriptions' && (
          <div className="bg-[#111113] border border-white/5 rounded-sm overflow-hidden" data-testid="subscriptions-table">
            <div className="p-4 border-b border-white/5">
              <p className="text-white/30 font-manrope text-xs">Active plans are billed automatically — a Square payment link is emailed to the client every month (or year). You can also send a link manually anytime.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    {['Client', 'Plan', 'Billing', 'Amount', 'Status', 'Visits', 'Next Billing', 'Last Link Sent', 'Actions'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-white/30 font-manrope">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.length === 0 ? (
                    <tr><td colSpan="9" className="px-4 py-8 text-center text-white/30 font-manrope text-sm">No subscriptions yet — created automatically when a client purchases a plan in the portal or app</td></tr>
                  ) : subscriptions.map(s => (
                    <tr key={s.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`subscription-row-${s.id}`}>
                      <td className="px-4 py-3">
                        <p className="text-white/80 font-manrope text-sm">{s.client_name || '—'}</p>
                        <p className="text-white/30 font-manrope text-xs">{s.client_email}</p>
                      </td>
                      <td className="px-4 py-3 text-white/60 font-manrope text-sm">{s.package_name}</td>
                      <td className="px-4 py-3 text-white/50 font-manrope text-xs uppercase">{s.billing_period}</td>
                      <td className="px-4 py-3 text-[#D4AF37] font-manrope font-semibold">${s.amount?.toLocaleString('en-CA', { minimumFractionDigits: 2 })}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold ${s.status === 'active' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-400'}`}>
                          {s.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => setVisitsSub(s)} className="flex items-center gap-1.5 text-white/60 hover:text-[#D4AF37] border border-white/10 hover:border-[#D4AF37]/40 px-2.5 py-1.5 rounded-sm font-manrope text-[10px] uppercase tracking-widest transition-colors" data-testid={`visits-btn-${s.id}`}>
                          <ClipboardList size={11} />
                          {s.visits_per_year ? `${s.visits_completed ?? 0}/${s.visits_per_year}` : 'Visits'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-white/50 font-manrope text-xs">{s.next_billing_date?.slice(0, 10) || '—'}</td>
                      <td className="px-4 py-3 text-white/30 font-manrope text-xs">{s.last_billed_at ? formatDate(s.last_billed_at) : 'Never'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button onClick={() => sendSubscriptionLink(s.id)} className="flex items-center gap-1.5 text-[#D4AF37]/70 hover:text-[#D4AF37] border border-[#D4AF37]/20 px-2.5 py-1.5 rounded-sm font-manrope text-[10px] uppercase tracking-widest transition-colors" data-testid={`send-link-${s.id}`}>
                            <Send size={11} /> Send Link
                          </button>
                          <button onClick={() => toggleSubscription(s)} className={`px-2.5 py-1.5 rounded-sm font-manrope text-[10px] uppercase tracking-widest border transition-colors ${s.status === 'active' ? 'text-red-400/70 hover:text-red-400 border-red-400/20' : 'text-green-500/70 hover:text-green-500 border-green-500/20'}`} data-testid={`toggle-sub-${s.id}`}>
                            {s.status === 'active' ? 'Cancel' : 'Reactivate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {visitsSub && <VisitsModal sub={visitsSub} headers={headers} onClose={() => { setVisitsSub(null); loadData(); }} />}
          </div>
        )}

        {/* Clients Tab */}
        {activeTab === 'clients' && (
          <div className="bg-[#111113] border border-white/5 rounded-sm overflow-hidden" data-testid="clients-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    {['Name', 'Email', 'Phone', 'Access Tier', 'UAN', 'Registered', 'Actions'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-white/30 font-manrope">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {clients.length === 0 ? (
                    <tr><td colSpan="7" className="px-4 py-8 text-center text-white/30 font-manrope text-sm">No clients yet</td></tr>
                  ) : clients.map(c => {
                    const cTier = c.access_tier || (c.is_activated ? 2 : 1);
                    return (
                    <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3 text-white/80 font-manrope text-sm">{c.name}</td>
                      <td className="px-4 py-3 text-white/50 font-manrope text-sm">{c.email}</td>
                      <td className="px-4 py-3 text-white/50 font-manrope text-sm">{c.phone || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold ${cTier >= 2 ? 'bg-green-500/10 text-green-500' : 'bg-[#D4AF37]/10 text-[#D4AF37]'}`} data-testid={`client-tier-${c.id}`}>
                          {cTier >= 2 ? 'Tier 2 — Project' : 'Tier 1 — Standard'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[#D4AF37] font-manrope text-xs font-mono">{c.uan || c.uan_code || '—'}</td>
                      <td className="px-4 py-3 text-white/30 font-manrope text-xs">{formatDate(c.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button onClick={() => openClientDetail(c.id)} className="text-[#D4AF37]/70 hover:text-[#D4AF37] border border-[#D4AF37]/20 px-2.5 py-1.5 rounded-sm font-manrope text-[10px] uppercase tracking-widest transition-colors" data-testid={`view-client-${c.id}`}>
                            View CRM
                          </button>
                          <button onClick={() => setTeamClient(c)} className="text-white/50 hover:text-[#D4AF37] border border-white/10 hover:border-[#D4AF37]/40 px-2.5 py-1.5 rounded-sm font-manrope text-[10px] uppercase tracking-widest transition-colors" data-testid={`team-btn-${c.id}`}>
                            Team{(c.assigned_staff_ids?.length || 0) > 0 ? ` (${c.assigned_staff_ids.length})` : ''}
                          </button>
                          <button onClick={() => setClientTier(c, cTier >= 2 ? 1 : 2)} className={`px-2.5 py-1.5 rounded-sm font-manrope text-[10px] uppercase tracking-widest border transition-colors ${cTier >= 2 ? 'text-yellow-400/70 hover:text-yellow-400 border-yellow-400/20' : 'text-green-500/70 hover:text-green-500 border-green-500/20'}`} data-testid={`tier-toggle-${c.id}`}>
                            {cTier >= 2 ? 'Set Tier 1' : 'Set Tier 2'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ); })}
                </tbody>
              </table>
            </div>
            {teamClient && (
              <TeamModal
                client={teamClient}
                staff={staff.filter(s => s.is_active !== false)}
                headers={headers}
                onClose={() => setTeamClient(null)}
                onSaved={() => { setTeamClient(null); loadData(); }}
              />
            )}
          </div>
        )}

        {/* Staff Tab */}
        {activeTab === 'staff' && (
          <div data-testid="staff-section">
            <form onSubmit={createStaff} className="bg-[#111113] border border-white/5 rounded-sm p-5 mb-6">
              <h3 className="font-manrope text-white/60 text-xs uppercase tracking-widest mb-4">Add Staff Member</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <input required placeholder="Full name" value={staffForm.name} onChange={e => setStaffForm({ ...staffForm, name: e.target.value })} className="bg-[#1a1a1a] border border-white/10 focus:border-[#D4AF37] focus:outline-none px-3 py-2.5 text-white font-manrope text-sm rounded-sm" data-testid="staff-name-input" />
                <input required type="email" placeholder="Email" value={staffForm.email} onChange={e => setStaffForm({ ...staffForm, email: e.target.value })} className="bg-[#1a1a1a] border border-white/10 focus:border-[#D4AF37] focus:outline-none px-3 py-2.5 text-white font-manrope text-sm rounded-sm" data-testid="staff-email-input" />
                <input required type="password" placeholder="Password" value={staffForm.password} onChange={e => setStaffForm({ ...staffForm, password: e.target.value })} className="bg-[#1a1a1a] border border-white/10 focus:border-[#D4AF37] focus:outline-none px-3 py-2.5 text-white font-manrope text-sm rounded-sm" data-testid="staff-password-input" />
                <input placeholder="Title (e.g. Lead Designer)" value={staffForm.title} onChange={e => setStaffForm({ ...staffForm, title: e.target.value })} className="bg-[#1a1a1a] border border-white/10 focus:border-[#D4AF37] focus:outline-none px-3 py-2.5 text-white font-manrope text-sm rounded-sm" data-testid="staff-title-input" />
                <select value={staffForm.role} onChange={e => setStaffForm({ ...staffForm, role: e.target.value })} className="bg-[#1a1a1a] border border-white/10 focus:border-[#D4AF37] focus:outline-none px-3 py-2.5 text-white font-manrope text-sm rounded-sm" data-testid="staff-role-select">
                  {['admin', 'customer_service', 'project_manager', 'designer', 'contractor', 'content_manager'].map(r => (
                    <option key={r} value={r}>{r.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
              <button type="submit" className="mt-4 bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest px-6 py-2.5 rounded-sm hover:bg-[#C5A059] transition-colors" data-testid="create-staff-btn">Add Staff</button>
            </form>
            <div className="bg-[#111113] border border-white/5 rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/5">
                      {['Staff Member', 'Title', 'Role', 'Status', 'Actions'].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-white/30 font-manrope">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {staff.map(s => (
                      <tr key={s.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`staff-row-${s.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center overflow-hidden">
                              {s.profile_picture_url ? <img src={s.profile_picture_url} alt="" className="w-full h-full object-cover" /> : <span className="text-[#D4AF37] font-playfair text-sm">{s.name?.charAt(0)}</span>}
                            </div>
                            <div>
                              <p className="text-white/80 font-manrope text-sm">{s.name}</p>
                              <p className="text-white/30 font-manrope text-xs">{s.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-white/50 font-manrope text-sm">{s.title || '—'}</td>
                        <td className="px-4 py-3">
                          <select value={s.role} onChange={e => updateStaffField(s.id, 'role', e.target.value)} disabled={s.role === 'super_admin'} className="bg-[#1a1a1a] border border-white/10 text-white/60 font-manrope text-xs rounded-sm px-2 py-1.5 disabled:opacity-50" data-testid={`role-select-${s.id}`}>
                            {['super_admin', 'admin', 'customer_service', 'project_manager', 'designer', 'contractor', 'content_manager'].map(r => (
                              <option key={r} value={r}>{r.replace('_', ' ')}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold ${s.status !== 'disabled' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-400'}`}>
                            {s.status || 'active'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {s.role !== 'super_admin' && (
                            <button onClick={() => updateStaffField(s.id, 'status', s.status === 'disabled' ? 'active' : 'disabled')} className={`px-2.5 py-1.5 rounded-sm font-manrope text-[10px] uppercase tracking-widest border transition-colors ${s.status === 'disabled' ? 'text-green-500/70 border-green-500/20' : 'text-red-400/70 border-red-400/20'}`} data-testid={`staff-toggle-${s.id}`}>
                              {s.status === 'disabled' ? 'Enable' : 'Disable'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Audit Log Tab */}
        {activeTab === 'audit' && (
          <div className="bg-[#111113] border border-white/5 rounded-sm overflow-hidden" data-testid="audit-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    {['When', 'Staff Member', 'Action', 'Entity', 'Details'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-white/30 font-manrope">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {auditLog.length === 0 ? (
                    <tr><td colSpan="5" className="px-4 py-8 text-center text-white/30 font-manrope text-sm">No administrative actions recorded yet</td></tr>
                  ) : auditLog.map(a => (
                    <tr key={a.id} className="border-b border-white/5">
                      <td className="px-4 py-3 text-white/30 font-manrope text-xs whitespace-nowrap">{formatDate(a.created_at)}</td>
                      <td className="px-4 py-3 text-white/60 font-manrope text-sm">{a.admin_name || a.admin_email}</td>
                      <td className="px-4 py-3"><span className="text-[#D4AF37] font-manrope text-xs uppercase tracking-widest">{a.action?.replace(/_/g, ' ')}</span></td>
                      <td className="px-4 py-3 text-white/40 font-manrope text-xs uppercase">{a.entity}</td>
                      <td className="px-4 py-3 text-white/40 font-manrope text-xs">{a.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Service Plans Tab */}
        {activeTab === 'plans' && (
          <div data-testid="plans-section">
            <div className="flex justify-between items-center mb-6">
              <p className="text-white/30 font-manrope text-xs max-w-xl">Control all package pricing, visits and features here — changes publish instantly to the website, client portal and mobile app. No code changes required.</p>
              <button onClick={() => setPlanEdit({ name: '', category: 'cleaning', description: '', monthly: 0, annual_full: 0, annual: 0, per_visit_price: '', visits_per_year: 0, features: '', active: true, order: 99 })} className="bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest px-5 py-2.5 rounded-sm hover:bg-[#C5A059] transition-colors whitespace-nowrap" data-testid="new-plan-btn">+ New Plan</button>
            </div>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
              {servicePlans.map(p => (
                <div key={p.id} className={`bg-[#111113] border rounded-sm p-5 ${p.active ? 'border-white/5' : 'border-red-500/20 opacity-60'}`} data-testid={`plan-card-${p.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h4 className="font-playfair text-white text-base">{p.name}</h4>
                    <span className="text-[8px] uppercase tracking-widest font-manrope text-[#D4AF37]/70 border border-[#D4AF37]/20 rounded-sm px-2 py-1">{p.category}</span>
                  </div>
                  <p className="text-[#D4AF37] font-playfair text-xl font-bold mb-1">${p.monthly?.toLocaleString('en-CA', { minimumFractionDigits: 2 })}<span className="text-xs text-white/40 font-manrope font-normal">/mo</span></p>
                  <p className="text-white/40 font-manrope text-xs mb-1">${p.annual?.toLocaleString('en-CA', { minimumFractionDigits: 2 })}/yr annual contract <span className="line-through text-white/20">${p.annual_full?.toLocaleString('en-CA', { minimumFractionDigits: 2 })}</span></p>
                  <p className="text-white/40 font-manrope text-xs mb-3">{p.visits_per_year || 0} visits/year{p.per_visit_price ? ` · $${p.per_visit_price}/visit` : ''} · {p.active ? 'Active' : 'Inactive'}</p>
                  <button onClick={() => setPlanEdit({ ...p, features: (p.features || []).join('\n') })} className="w-full border border-[#D4AF37]/40 text-[#D4AF37] font-manrope font-semibold text-[10px] uppercase tracking-widest py-2 rounded-sm hover:bg-[#D4AF37] hover:text-black transition-colors" data-testid={`edit-plan-${p.id}`}>Edit Plan</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Plan Edit Modal */}
        {planEdit && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm" data-testid="plan-edit-modal">
            <form onSubmit={savePlan} className="bg-[#111113] border border-[#D4AF37]/30 rounded-sm max-w-2xl w-full p-8 max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-playfair text-xl text-white">{planEdit.id ? 'Edit Plan' : 'New Plan'}</h3>
                <button type="button" onClick={() => setPlanEdit(null)} className="text-white/40 hover:text-white" data-testid="close-plan-modal"><X size={18} /></button>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <input required placeholder="Plan name" value={planEdit.name} onChange={e => setPlanEdit({ ...planEdit, name: e.target.value })} className="col-span-2 bg-[#1a1a1a] border border-white/10 focus:border-[#D4AF37] focus:outline-none px-3 py-2.5 text-white font-manrope text-sm rounded-sm" data-testid="plan-name-input" />
                <select value={planEdit.category} onChange={e => setPlanEdit({ ...planEdit, category: e.target.value })} className="bg-[#1a1a1a] border border-white/10 px-3 py-2.5 text-white font-manrope text-sm rounded-sm" data-testid="plan-category-select">
                  <option value="warranty">Warranty & Maintenance (Warranty)</option>
                  <option value="maintenance">Warranty & Maintenance (Maintenance)</option>
                  <option value="cleaning">Cleaning Services</option>
                </select>
                <label className="flex items-center gap-2 text-white/60 font-manrope text-sm">
                  <input type="checkbox" checked={!!planEdit.active} onChange={e => setPlanEdit({ ...planEdit, active: e.target.checked })} data-testid="plan-active-checkbox" /> Active (visible & purchasable)
                </label>
                <div>
                  <label className="text-white/30 font-manrope text-[10px] uppercase tracking-widest">Monthly price ($)</label>
                  <input type="number" step="0.01" value={planEdit.monthly} onChange={e => setPlanEdit({ ...planEdit, monthly: e.target.value })} className="w-full bg-[#1a1a1a] border border-white/10 px-3 py-2.5 text-white font-manrope text-sm rounded-sm" data-testid="plan-monthly-input" />
                </div>
                <div>
                  <label className="text-white/30 font-manrope text-[10px] uppercase tracking-widest">Full yearly price ($)</label>
                  <input type="number" step="0.01" value={planEdit.annual_full} onChange={e => setPlanEdit({ ...planEdit, annual_full: e.target.value })} className="w-full bg-[#1a1a1a] border border-white/10 px-3 py-2.5 text-white font-manrope text-sm rounded-sm" data-testid="plan-annualfull-input" />
                </div>
                <div>
                  <label className="text-white/30 font-manrope text-[10px] uppercase tracking-widest">Annual contract price ($, after discount)</label>
                  <input type="number" step="0.01" value={planEdit.annual} onChange={e => setPlanEdit({ ...planEdit, annual: e.target.value })} className="w-full bg-[#1a1a1a] border border-white/10 px-3 py-2.5 text-white font-manrope text-sm rounded-sm" data-testid="plan-annual-input" />
                </div>
                <div>
                  <label className="text-white/30 font-manrope text-[10px] uppercase tracking-widest">Visits per year</label>
                  <input type="number" value={planEdit.visits_per_year} onChange={e => setPlanEdit({ ...planEdit, visits_per_year: e.target.value })} className="w-full bg-[#1a1a1a] border border-white/10 px-3 py-2.5 text-white font-manrope text-sm rounded-sm" data-testid="plan-visits-input" />
                </div>
                <div>
                  <label className="text-white/30 font-manrope text-[10px] uppercase tracking-widest">Per-visit price ($, cleaning)</label>
                  <input type="number" step="0.01" value={planEdit.per_visit_price || ''} onChange={e => setPlanEdit({ ...planEdit, per_visit_price: e.target.value })} className="w-full bg-[#1a1a1a] border border-white/10 px-3 py-2.5 text-white font-manrope text-sm rounded-sm" data-testid="plan-pervisit-input" />
                </div>
              </div>
              <textarea rows={2} placeholder="Short description" value={planEdit.description} onChange={e => setPlanEdit({ ...planEdit, description: e.target.value })} className="w-full bg-[#1a1a1a] border border-white/10 px-3 py-2.5 text-white font-manrope text-sm rounded-sm mb-4" data-testid="plan-description-input" />
              <label className="text-white/30 font-manrope text-[10px] uppercase tracking-widest">Included features (one per line)</label>
              <textarea rows={8} value={planEdit.features} onChange={e => setPlanEdit({ ...planEdit, features: e.target.value })} className="w-full bg-[#1a1a1a] border border-white/10 px-3 py-2.5 text-white font-manrope text-xs rounded-sm mb-6" data-testid="plan-features-input" />
              <button type="submit" className="w-full bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest py-3 rounded-sm hover:bg-[#C5A059] transition-colors" data-testid="save-plan-btn">Save Plan</button>
            </form>
          </div>
        )}

        {/* Client CRM Detail Modal */}
        {clientDetail && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm" data-testid="client-detail-modal">
            <div className="bg-[#111113] border border-[#D4AF37]/30 rounded-sm max-w-3xl w-full p-8 max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-playfair text-xl text-white">{clientDetail.client.name}</h3>
                  <p className="text-white/40 font-manrope text-xs">{clientDetail.client.email} · {clientDetail.client.phone || 'no phone'}</p>
                </div>
                <button onClick={() => setClientDetail(null)} className="text-white/40 hover:text-white" data-testid="close-client-detail"><X size={18} /></button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <div className="bg-[#1a1a1a] rounded-sm p-3">
                  <p className="text-white/30 font-manrope text-[9px] uppercase tracking-widest mb-1">UAN</p>
                  <p className="text-[#D4AF37] font-mono text-xs font-bold">{clientDetail.client.uan || clientDetail.client.uan_code || '—'}</p>
                </div>
                <div className="bg-[#1a1a1a] rounded-sm p-3">
                  <p className="text-white/30 font-manrope text-[9px] uppercase tracking-widest mb-1">Access</p>
                  <p className={`font-manrope text-xs font-bold ${(clientDetail.client.access_tier || 1) >= 2 ? 'text-green-500' : 'text-[#D4AF37]'}`}>Tier {clientDetail.client.access_tier || 1}</p>
                  {clientDetail.client.tier_changed_by && <p className="text-white/25 font-manrope text-[9px] mt-1">by {clientDetail.client.tier_changed_by}<br/>{clientDetail.client.tier_changed_at ? formatDate(clientDetail.client.tier_changed_at) : ''}</p>}
                </div>
                <div className="bg-[#1a1a1a] rounded-sm p-3">
                  <p className="text-white/30 font-manrope text-[9px] uppercase tracking-widest mb-1">Registered</p>
                  <p className="text-white/60 font-manrope text-xs">{formatDate(clientDetail.client.created_at)}</p>
                </div>
                <div className="bg-[#1a1a1a] rounded-sm p-3">
                  <p className="text-white/30 font-manrope text-[9px] uppercase tracking-widest mb-1">Project Photos</p>
                  <p className="text-white/60 font-manrope text-xs">{clientDetail.project_photos_count}</p>
                </div>
              </div>
              {[
                { label: 'Bookings', rows: clientDetail.bookings, render: b => `${b.preferred_date || '—'} ${b.preferred_time || ''} · ${b.service_type} · ${b.status}${b.budget ? ` · Budget: ${b.budget}` : ''}${b.travel_fee ? ` · $${b.travel_fee}` : ''}` },
                { label: 'Payments', rows: clientDetail.payments, render: p => `${formatDate(p.created_at)} · ${p.package_name} · $${p.amount?.toLocaleString('en-CA', { minimumFractionDigits: 2 })} · ${p.payment_status}` },
                { label: 'Subscriptions / Plans', rows: clientDetail.subscriptions, render: s => `${s.package_name} · ${s.billing_period} · $${s.amount?.toLocaleString('en-CA', { minimumFractionDigits: 2 })} · ${s.status} · next: ${s.next_billing_date?.slice(0, 10)}` },
                { label: 'Documents', rows: clientDetail.documents, render: d => `${d.title || d.file_name || 'Document'} · ${formatDate(d.created_at)}` },
                { label: 'Maintenance / Service History', rows: clientDetail.maintenance, render: m => `${m.scheduled_date || '—'} · ${m.service_type || m.title || 'Service'} · ${m.status || ''}` },
              ].map(section => (
                <div key={section.label} className="mb-5">
                  <h4 className="text-white/40 font-manrope text-[10px] uppercase tracking-widest mb-2">{section.label} ({section.rows?.length || 0})</h4>
                  {(!section.rows || section.rows.length === 0) ? (
                    <p className="text-white/20 font-manrope text-xs">None</p>
                  ) : (
                    <div className="space-y-1.5">
                      {section.rows.slice(0, 6).map((r, i) => (
                        <p key={i} className="text-white/55 font-manrope text-xs bg-[#1a1a1a] rounded-sm px-3 py-2">{section.render(r)}</p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* UAN Codes Tab */}
        {activeTab === 'uan' && (
          <div data-testid="uan-management">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-manrope text-white/60 text-sm">{uanCodes.length} codes generated</h3>
              <div className="flex items-center gap-3">
                <select
                  value={uanQty}
                  onChange={e => setUanQty(Number(e.target.value))}
                  className="bg-[#111113] border border-white/10 text-white font-manrope text-xs rounded-sm px-3 py-2 focus:border-[#D4AF37] focus:outline-none"
                  data-testid="uan-qty-select"
                >
                  {[1, 5, 10, 25, 50].map(n => <option key={n} value={n}>{n} code{n > 1 ? 's' : ''}</option>)}
                </select>
                <button onClick={generateUAN} className="flex items-center gap-2 bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest px-4 py-2 rounded-sm hover:bg-[#C5A059] transition-all" data-testid="generate-uan-btn">
                  <Key size={14} /> Generate
                </button>
              </div>
            </div>
            {generatedCodes && (
              <div className="mb-6 bg-[#111113] border border-[#D4AF37]/30 rounded-sm p-5" data-testid="generated-codes-panel">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-manrope text-[#D4AF37] text-xs uppercase tracking-widest font-bold">{generatedCodes.length} New Code{generatedCodes.length > 1 ? 's' : ''} Generated</h4>
                  <div className="flex gap-2">
                    <button
                      onClick={async () => { try { await navigator.clipboard.writeText(generatedCodes.join('\n')); alert('Codes copied to clipboard'); } catch { alert('Copy failed — please select the codes manually'); } }}
                      className="text-xs font-manrope text-white/60 hover:text-[#D4AF37] border border-white/10 px-3 py-1.5 rounded-sm transition-colors"
                      data-testid="copy-codes-btn"
                    >Copy All</button>
                    <button
                      onClick={() => setGeneratedCodes(null)}
                      className="text-xs font-manrope text-white/40 hover:text-white px-2 py-1.5 transition-colors"
                      data-testid="close-codes-panel"
                    >✕</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  {generatedCodes.map(c => (
                    <span key={c} className="text-[#D4AF37] font-mono text-sm font-bold bg-black/40 border border-white/5 rounded-sm px-3 py-2 text-center">{c}</span>
                  ))}
                </div>
              </div>
            )}
            <div className="bg-[#111113] border border-white/5 rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/5">
                      {['Code', 'Status', 'Assigned To', 'Created', 'Used At'].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-white/30 font-manrope">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {uanCodes.length === 0 ? (
                      <tr><td colSpan="5" className="px-4 py-8 text-center text-white/30 font-manrope text-sm">No UAN codes generated yet</td></tr>
                    ) : uanCodes.map(u => (
                      <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="px-4 py-3 text-[#D4AF37] font-manrope text-sm font-mono font-bold">{u.code}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold ${u.is_used ? 'bg-green-500/10 text-green-500' : 'bg-blue-500/10 text-blue-400'}`}>
                            {u.is_used ? 'Used' : 'Available'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-white/50 font-manrope text-sm">{u.assigned_to || '—'}</td>
                        <td className="px-4 py-3 text-white/30 font-manrope text-xs">{formatDate(u.created_at)}</td>
                        <td className="px-4 py-3 text-white/30 font-manrope text-xs">{u.used_at ? formatDate(u.used_at) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div data-testid="messages-management" className="flex gap-4" style={{ height: 'calc(100vh - 300px)' }}>
            {/* Conversation List */}
            <div className="w-72 bg-[#111113] border border-white/5 rounded-sm overflow-hidden flex flex-col">
              <div className="p-3 border-b border-white/5">
                <h4 className="font-manrope text-white/60 text-xs uppercase tracking-widest">Conversations</h4>
              </div>
              <div className="flex-1 overflow-y-auto">
                {conversations.length === 0 ? (
                  <p className="p-4 text-white/20 font-manrope text-xs text-center">No conversations yet</p>
                ) : conversations.map(c => (
                  <button key={c.id} onClick={() => openConversation(c.client_id)}
                    className={`w-full text-left p-3 border-b border-white/5 hover:bg-white/[0.02] transition-all ${selectedConvo?.client_id === c.client_id ? 'bg-[#D4AF37]/5 border-l-2 border-l-[#D4AF37]' : ''}`}
                    data-testid={`convo-${c.client_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-white/80 font-manrope text-sm font-semibold">{c.client_name}</span>
                      {c.unread_admin > 0 && <span className="bg-[#D4AF37] text-black w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold">{c.unread_admin}</span>}
                    </div>
                    <p className="text-white/30 font-manrope text-xs mt-1 truncate">{c.last_message}</p>
                  </button>
                ))}
              </div>
              <div className="p-3 border-t border-white/5">
                <select onChange={e => { if (e.target.value) startNewConvo(e.target.value); e.target.value = ''; }} className="w-full bg-transparent border border-white/10 rounded-sm px-2 py-1.5 text-white font-manrope text-xs focus:outline-none focus:border-[#D4AF37]" data-testid="new-convo-select">
                  <option value="" className="bg-[#111]">New message to...</option>
                  {clientOptions.map(c => <option key={c.id} value={c.id} className="bg-[#111]">{c.label}</option>)}
                </select>
              </div>
            </div>

            {/* Chat Area */}
            <div className="flex-1 bg-[#111113] border border-white/5 rounded-sm flex flex-col">
              {!selectedConvo ? (
                <div className="flex-1 flex items-center justify-center">
                  <p className="text-white/15 font-manrope text-sm">Select a conversation or start a new one</p>
                </div>
              ) : (
                <>
                  <div className="p-4 border-b border-white/5">
                    <h4 className="font-manrope text-white text-sm font-semibold">{selectedConvo.client_name || 'Client'}</h4>
                    <p className="text-white/30 font-manrope text-xs">{selectedConvo.client_email || ''}</p>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {convoMessages.length === 0 && <p className="text-white/15 font-manrope text-xs text-center py-8">No messages yet. Send the first message.</p>}
                    {convoMessages.map(msg => (
                      <div key={msg.id} className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[70%] px-4 py-2.5 rounded-sm ${msg.sender_type === 'admin' ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/20' : 'bg-white/[0.03] border border-white/5'}`}>
                          {msg.content && <p className="text-white/70 font-manrope text-sm">{msg.content}</p>}
                          {msg.attachment && (
                            <div className="mt-1.5 bg-black/20 rounded-sm p-2">
                              {msg.attachment.content_type?.startsWith('image/') ? (
                                <img src={`${API}/files/${msg.attachment.storage_path}`} alt={msg.attachment.filename} className="max-w-full max-h-36 rounded-sm" />
                              ) : (
                                <div className="flex items-center gap-2">
                                  <FileText size={12} className="text-[#D4AF37]/50 shrink-0" />
                                  <span className="text-white/40 font-manrope text-xs truncate">{msg.attachment.filename}</span>
                                </div>
                              )}
                              <a href={`${API}/files/${msg.attachment.storage_path}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 mt-1 text-[#D4AF37]/50 hover:text-[#D4AF37] font-manrope text-[9px] uppercase tracking-wider transition-colors">
                                <Download size={9} /> Download
                              </a>
                            </div>
                          )}
                          <p className={`font-manrope text-[10px] mt-1 ${msg.sender_type === 'admin' ? 'text-[#D4AF37]/40 text-right' : 'text-white/20'}`}>
                            {msg.sender_name} &middot; {formatDate(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-3 border-t border-white/5 flex gap-2">
                    <input type="file" id="admin-attach" className="hidden" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const fd = new FormData();
                        fd.append('file', file);
                        fd.append('context', 'message');
                        const res = await axios.post(`${API}/upload`, fd, { headers });
                        await axios.post(`${API}/admin/messages`, { client_id: selectedConvo.client_id, content: '', attachment_id: res.data.id }, { headers });
                        openConversation(selectedConvo.client_id);
                      } catch (err) { alert('Upload failed'); }
                      e.target.value = '';
                    }} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" />
                    <button onClick={() => document.getElementById('admin-attach').click()} className="text-white/30 hover:text-[#D4AF37] border border-white/10 hover:border-[#D4AF37]/30 px-2.5 rounded-sm transition-all" title="Attach file" data-testid="admin-attach-btn">
                      <Upload size={14} />
                    </button>
                    <input value={adminNewMsg} onChange={e => setAdminNewMsg(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendAdminMessage()} placeholder="Type a message..." className="flex-1 bg-transparent border border-white/10 rounded-sm px-3 py-2 text-white font-manrope text-sm focus:outline-none focus:border-[#D4AF37]" data-testid="admin-message-input" />
                    <button onClick={sendAdminMessage} disabled={!adminNewMsg.trim()} className="bg-[#D4AF37] hover:bg-[#C5A059] text-black px-4 rounded-sm transition-all disabled:opacity-30" data-testid="admin-send-msg-btn">
                      <Send size={14} />
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div data-testid="documents-management">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-manrope text-white/60 text-sm">{documents.length} documents</h3>
              <button onClick={() => setShowAddDoc(!showAddDoc)} className="flex items-center gap-2 bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest px-4 py-2 rounded-sm hover:bg-[#C5A059] transition-all" data-testid="add-document-btn">
                <Plus size={14} /> Add Document
              </button>
            </div>

            {showAddDoc && (
              <div className="bg-[#111113] border border-[#D4AF37]/20 rounded-sm p-6 mb-6 space-y-4" data-testid="add-document-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <select value={newDoc.client_id} onChange={e => setNewDoc(p => ({ ...p, client_id: e.target.value }))} className="bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white font-manrope text-sm" data-testid="doc-client-select">
                    <option value="" className="bg-[#111]">Select Client...</option>
                    {clientOptions.map(c => <option key={c.id} value={c.id} className="bg-[#111]">{c.label}</option>)}
                  </select>
                  <select value={newDoc.doc_type} onChange={e => setNewDoc(p => ({ ...p, doc_type: e.target.value }))} className="bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white font-manrope text-sm" data-testid="doc-type-select">
                    <option value="contract" className="bg-[#111]">Contract</option>
                    <option value="proposal" className="bg-[#111]">Proposal</option>
                    <option value="warranty" className="bg-[#111]">Warranty</option>
                    <option value="invoice" className="bg-[#111]">Invoice</option>
                    <option value="other" className="bg-[#111]">Other</option>
                  </select>
                </div>
                <input value={newDoc.title} onChange={e => setNewDoc(p => ({ ...p, title: e.target.value }))} placeholder="Document Title" className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white placeholder:text-white/20 font-manrope text-sm" data-testid="doc-title-input" />
                <input value={newDoc.file_url} onChange={e => setNewDoc(p => ({ ...p, file_url: e.target.value }))} placeholder="Document URL (PDF, Google Doc, etc.)" className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white placeholder:text-white/20 font-manrope text-sm" data-testid="doc-url-input" />
                <input value={newDoc.notes} onChange={e => setNewDoc(p => ({ ...p, notes: e.target.value }))} placeholder="Notes (optional)" className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white placeholder:text-white/20 font-manrope text-sm" data-testid="doc-notes-input" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <select value={newDoc.folder} onChange={e => setNewDoc(p => ({ ...p, folder: e.target.value }))} className="bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white font-manrope text-sm" data-testid="doc-folder-select">
                    <option value="general" className="bg-[#111]">General</option>
                    <option value="pre-construction" className="bg-[#111]">Pre-Construction</option>
                    <option value="installation" className="bg-[#111]">Installation</option>
                    <option value="completion" className="bg-[#111]">Completion</option>
                    <option value="warranty" className="bg-[#111]">Warranty</option>
                  </select>
                  <label className="flex items-center gap-3 cursor-pointer py-2">
                    <input type="checkbox" checked={newDoc.signature_required} onChange={e => setNewDoc(p => ({ ...p, signature_required: e.target.checked }))} className="accent-[#D4AF37]" data-testid="doc-sig-required" />
                    <span className="text-white/60 font-manrope text-sm">Requires Digital Signature</span>
                  </label>
                </div>
                <div className="flex gap-3">
                  <button onClick={addDocument} className="bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest px-6 py-2 rounded-sm hover:bg-[#C5A059] transition-all" data-testid="save-document-btn">Save</button>
                  <button onClick={() => setShowAddDoc(false)} className="text-white/40 font-manrope text-xs uppercase tracking-widest px-6 py-2 border border-white/10 rounded-sm hover:border-white/30 transition-all">Cancel</button>
                </div>
              </div>
            )}

            <div className="bg-[#111113] border border-white/5 rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/5">
                      {['Title', 'Type', 'Folder', 'Client', 'Signature', 'URL', 'Created', 'Actions'].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-white/30 font-manrope">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {documents.length === 0 ? (
                      <tr><td colSpan="8" className="px-4 py-8 text-center text-white/30 font-manrope text-sm">No documents yet</td></tr>
                    ) : documents.map(d => (
                      <tr key={d.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="px-4 py-3 text-white/80 font-manrope text-sm">{d.title}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold bg-indigo-500/10 text-indigo-400">{d.doc_type}</span>
                        </td>
                        <td className="px-4 py-3 text-white/40 font-manrope text-xs uppercase">{d.folder || 'general'}</td>
                        <td className="px-4 py-3 text-white/50 font-manrope text-sm">{d.client_name || d.client_email || '—'}</td>
                        <td className="px-4 py-3">
                          {d.signature_required ? (
                            <span className={`px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold ${d.signature_status === 'signed' ? 'bg-green-500/10 text-green-500' : 'bg-yellow-500/10 text-yellow-500'}`}>
                              {d.signature_status === 'signed' ? 'Signed' : 'Pending'}
                            </span>
                          ) : <span className="text-white/20 font-manrope text-xs">—</span>}
                        </td>
                        <td className="px-4 py-3"><a href={d.file_url} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] font-manrope text-xs hover:underline">View</a></td>
                        <td className="px-4 py-3 text-white/30 font-manrope text-xs">{formatDate(d.created_at)}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => deleteDocument(d.id)} className="text-white/20 hover:text-red-400 transition-colors" data-testid={`delete-doc-${d.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Promotions Tab */}
        {activeTab === 'promotions' && (
          <div data-testid="promotions-management">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-manrope text-white/60 text-sm">{promotions.length} promotions</h3>
              <button onClick={() => setShowAddPromo(!showAddPromo)} className="flex items-center gap-2 bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest px-4 py-2 rounded-sm hover:bg-[#C5A059] transition-all" data-testid="add-promotion-btn">
                <Plus size={14} /> Add Promotion
              </button>
            </div>

            {showAddPromo && (
              <div className="bg-[#111113] border border-[#D4AF37]/20 rounded-sm p-6 mb-6 space-y-4" data-testid="add-promotion-form">
                <input value={newPromo.title} onChange={e => setNewPromo(p => ({ ...p, title: e.target.value }))} placeholder="Promotion Title" className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white placeholder:text-white/20 font-manrope text-sm" data-testid="promo-title-input" />
                <input value={newPromo.description} onChange={e => setNewPromo(p => ({ ...p, description: e.target.value }))} placeholder="Description" className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white placeholder:text-white/20 font-manrope text-sm" data-testid="promo-desc-input" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <select value={newPromo.discount_type} onChange={e => setNewPromo(p => ({ ...p, discount_type: e.target.value }))} className="bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white font-manrope text-sm" data-testid="promo-type-select">
                    <option value="percentage" className="bg-[#111]">Percentage Off</option>
                    <option value="fixed" className="bg-[#111]">Fixed Amount Off</option>
                    <option value="info" className="bg-[#111]">Info Only</option>
                  </select>
                  <input type="number" value={newPromo.discount_value} onChange={e => setNewPromo(p => ({ ...p, discount_value: parseFloat(e.target.value) || 0 }))} placeholder="Discount Value" className="bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white placeholder:text-white/20 font-manrope text-sm" data-testid="promo-value-input" />
                  <input value={newPromo.promo_code} onChange={e => setNewPromo(p => ({ ...p, promo_code: e.target.value }))} placeholder="Promo Code (optional)" className="bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white placeholder:text-white/20 font-manrope text-sm" data-testid="promo-code-input" />
                </div>
                <input type="date" value={newPromo.valid_until} onChange={e => setNewPromo(p => ({ ...p, valid_until: e.target.value }))} className="bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white font-manrope text-sm" data-testid="promo-expiry-input" />
                <div className="flex gap-3">
                  <button onClick={addPromotion} className="bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest px-6 py-2 rounded-sm hover:bg-[#C5A059] transition-all" data-testid="save-promotion-btn">Save</button>
                  <button onClick={() => setShowAddPromo(false)} className="text-white/40 font-manrope text-xs uppercase tracking-widest px-6 py-2 border border-white/10 rounded-sm hover:border-white/30 transition-all">Cancel</button>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {promotions.length === 0 ? (
                <div className="bg-[#111113] border border-white/5 rounded-sm p-8 text-center text-white/30 font-manrope text-sm">No promotions yet</div>
              ) : promotions.map(promo => (
                <div key={promo.id} className={`bg-[#111113] border rounded-sm p-5 ${promo.is_active ? 'border-[#D4AF37]/15' : 'border-white/5 opacity-50'}`} data-testid={`promo-${promo.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-manrope font-bold text-white text-sm">{promo.title}</h4>
                        {promo.discount_type !== 'info' && promo.discount_value > 0 && (
                          <span className="bg-[#D4AF37] text-black font-manrope font-bold text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm">
                            {promo.discount_type === 'percentage' ? `${promo.discount_value}% OFF` : `$${promo.discount_value} OFF`}
                          </span>
                        )}
                        <span className={`px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold ${promo.is_active ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-400'}`}>
                          {promo.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="text-white/40 font-manrope text-xs mb-2">{promo.description}</p>
                      <div className="flex items-center gap-4 text-white/30 font-manrope text-xs">
                        {promo.promo_code && <span>Code: <span className="text-[#D4AF37] font-mono">{promo.promo_code}</span></span>}
                        {promo.valid_until && <span>Expires: {promo.valid_until}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => togglePromotion(promo.id, promo.is_active)} className="text-white/30 hover:text-[#D4AF37] transition-colors" data-testid={`toggle-promo-${promo.id}`}>
                        {promo.is_active ? <X size={14} /> : <Check size={14} />}
                      </button>
                      <button onClick={() => deletePromotion(promo.id)} className="text-white/20 hover:text-red-400 transition-colors" data-testid={`delete-promo-${promo.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Maintenance Tab */}
        {activeTab === 'maintenance' && (
          <div data-testid="maintenance-management">
            <WarrantyRegistrationsBlock headers={headers} />
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-manrope text-white/60 text-sm">{maintenance.length} records</h3>
              <button onClick={() => setShowAddMaint(!showAddMaint)} className="flex items-center gap-2 bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest px-4 py-2 rounded-sm hover:bg-[#C5A059] transition-all" data-testid="add-maintenance-btn">
                <Plus size={14} /> Add Record
              </button>
            </div>

            {showAddMaint && (
              <div className="bg-[#111113] border border-[#D4AF37]/20 rounded-sm p-6 mb-6 space-y-4" data-testid="add-maintenance-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <select value={newMaint.client_id} onChange={e => setNewMaint(p => ({ ...p, client_id: e.target.value }))} className="bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white font-manrope text-sm" data-testid="maint-client-select">
                    <option value="" className="bg-[#111]">Select Client...</option>
                    {clientOptions.map(c => <option key={c.id} value={c.id} className="bg-[#111]">{c.label}</option>)}
                  </select>
                  <select value={newMaint.service_type} onChange={e => setNewMaint(p => ({ ...p, service_type: e.target.value }))} className="bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white font-manrope text-sm" data-testid="maint-type-select">
                    <option value="routine" className="bg-[#111]">Routine Check</option>
                    <option value="repair" className="bg-[#111]">Repair</option>
                    <option value="inspection" className="bg-[#111]">Inspection</option>
                    <option value="upgrade" className="bg-[#111]">Upgrade</option>
                  </select>
                </div>
                <input value={newMaint.description} onChange={e => setNewMaint(p => ({ ...p, description: e.target.value }))} placeholder="Description" className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white placeholder:text-white/20 font-manrope text-sm" data-testid="maint-desc-input" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input type="date" value={newMaint.scheduled_date} onChange={e => setNewMaint(p => ({ ...p, scheduled_date: e.target.value }))} className="bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white font-manrope text-sm" data-testid="maint-date-input" />
                  <input value={newMaint.technician} onChange={e => setNewMaint(p => ({ ...p, technician: e.target.value }))} placeholder="Technician Name" className="bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white placeholder:text-white/20 font-manrope text-sm" data-testid="maint-tech-input" />
                </div>
                <input value={newMaint.notes} onChange={e => setNewMaint(p => ({ ...p, notes: e.target.value }))} placeholder="Notes (optional)" className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white placeholder:text-white/20 font-manrope text-sm" data-testid="maint-notes-input" />
                <div className="flex gap-3">
                  <button onClick={addMaintenance} className="bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest px-6 py-2 rounded-sm hover:bg-[#C5A059] transition-all" data-testid="save-maintenance-btn">Save</button>
                  <button onClick={() => setShowAddMaint(false)} className="text-white/40 font-manrope text-xs uppercase tracking-widest px-6 py-2 border border-white/10 rounded-sm hover:border-white/30 transition-all">Cancel</button>
                </div>
              </div>
            )}

            <div className="bg-[#111113] border border-white/5 rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/5">
                      {['Client', 'Type', 'Description', 'Scheduled', 'Technician', 'Status', 'Actions'].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-white/30 font-manrope">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {maintenance.length === 0 ? (
                      <tr><td colSpan="7" className="px-4 py-8 text-center text-white/30 font-manrope text-sm">No maintenance records</td></tr>
                    ) : maintenance.map(m => (
                      <tr key={m.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="px-4 py-3 text-white/80 font-manrope text-sm">{m.client_name || '—'}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold bg-[#D4AF37]/10 text-[#D4AF37]">{m.service_type}</span>
                        </td>
                        <td className="px-4 py-3 text-white/50 font-manrope text-sm max-w-[200px] truncate">{m.description}</td>
                        <td className="px-4 py-3 text-white/50 font-manrope text-sm">{m.scheduled_date || '—'}</td>
                        <td className="px-4 py-3 text-white/50 font-manrope text-sm">{m.technician || '—'}</td>
                        <td className="px-4 py-3">
                          <div className="relative group">
                            <button className={`${maintenanceStatusColors[m.status]?.bg || 'bg-white/5'} ${maintenanceStatusColors[m.status]?.text || 'text-white/40'} px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold flex items-center gap-1`} data-testid={`maint-status-${m.id}`}>
                              {m.status} <ChevronDown size={10} />
                            </button>
                            <div className="absolute top-full left-0 mt-1 bg-[#1a1a1a] border border-white/10 rounded-sm shadow-xl z-10 hidden group-hover:block min-w-[130px]">
                              {['scheduled', 'in_progress', 'completed', 'cancelled'].map(s => (
                                <button key={s} onClick={() => updateMaintenanceStatus(m.id, s)} className="block w-full text-left px-3 py-2 text-xs font-manrope text-white/60 hover:text-[#D4AF37] hover:bg-white/5 uppercase tracking-widest">
                                  {s.replace('_', ' ')}
                                </button>
                              ))}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <button onClick={() => deleteMaintenance(m.id)} className="text-white/20 hover:text-red-400 transition-colors" data-testid={`delete-maint-${m.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Project Photos Tab */}
        {activeTab === 'photos' && (
          <div data-testid="photos-management">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-manrope text-white/60 text-sm">{projectPhotos.length} photos</h3>
              <button onClick={() => setShowAddPhoto(!showAddPhoto)} className="flex items-center gap-2 bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest px-4 py-2 rounded-sm hover:bg-[#C5A059] transition-all" data-testid="add-photo-btn">
                <Plus size={14} /> Add Photo
              </button>
            </div>

            {showAddPhoto && (
              <div className="bg-[#111113] border border-[#D4AF37]/20 rounded-sm p-6 mb-6 space-y-4" data-testid="add-photo-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <select value={newPhoto.client_id} onChange={e => setNewPhoto(p => ({ ...p, client_id: e.target.value }))} className="bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white font-manrope text-sm" data-testid="photo-client-select">
                    <option value="" className="bg-[#111]">Select Client...</option>
                    {clientOptions.map(c => <option key={c.id} value={c.id} className="bg-[#111]">{c.label}</option>)}
                  </select>
                  <select value={newPhoto.phase} onChange={e => setNewPhoto(p => ({ ...p, phase: e.target.value }))} className="bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white font-manrope text-sm" data-testid="photo-phase-select">
                    <option value="before" className="bg-[#111]">Before</option>
                    <option value="during" className="bg-[#111]">In Progress</option>
                    <option value="completed" className="bg-[#111]">Completed</option>
                  </select>
                </div>
                <input value={newPhoto.title} onChange={e => setNewPhoto(p => ({ ...p, title: e.target.value }))} placeholder="Photo Title" className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white placeholder:text-white/20 font-manrope text-sm" data-testid="photo-title-input" />
                <input value={newPhoto.image_url} onChange={e => setNewPhoto(p => ({ ...p, image_url: e.target.value }))} placeholder="Image URL" className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white placeholder:text-white/20 font-manrope text-sm" data-testid="photo-url-input" />
                <input value={newPhoto.description} onChange={e => setNewPhoto(p => ({ ...p, description: e.target.value }))} placeholder="Description (optional)" className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white placeholder:text-white/20 font-manrope text-sm" data-testid="photo-desc-input" />
                <div className="flex gap-3">
                  <button onClick={addProjectPhoto} className="bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest px-6 py-2 rounded-sm hover:bg-[#C5A059] transition-all" data-testid="save-photo-btn">Save</button>
                  <button onClick={() => setShowAddPhoto(false)} className="text-white/40 font-manrope text-xs uppercase tracking-widest px-6 py-2 border border-white/10 rounded-sm hover:border-white/30 transition-all">Cancel</button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {projectPhotos.length === 0 ? (
                <div className="col-span-full bg-[#111113] border border-white/5 rounded-sm p-8 text-center text-white/30 font-manrope text-sm">No project photos yet</div>
              ) : projectPhotos.map(photo => (
                <div key={photo.id} className="bg-[#111113] border border-white/5 rounded-sm overflow-hidden group relative" data-testid={`photo-${photo.id}`}>
                  <div className="aspect-square">
                    <img src={photo.image_url} alt={photo.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-3">
                    <p className="text-white/80 font-manrope text-xs font-semibold truncate">{photo.title}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className={`text-[10px] uppercase tracking-widest font-manrope font-bold ${photo.phase === 'completed' ? 'text-green-400' : photo.phase === 'during' ? 'text-indigo-400' : 'text-yellow-400'}`}>{photo.phase}</span>
                      <span className="text-white/20 font-manrope text-[10px]">{photo.client_name}</span>
                    </div>
                  </div>
                  <button onClick={() => deleteProjectPhoto(photo.id)} className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm text-white/50 hover:text-red-400 w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all" data-testid={`delete-photo-${photo.id}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Document Library Tab */}
        {activeTab === 'library' && <AdminLibraryTab headers={headers} clientOptions={clientOptions} />}
        {activeTab === 'signatures' && <SignaturesTab headers={headers} clients={clients} />}
      </div>
    </div>
  );
}


function AdminLibraryTab({ headers, clientOptions }) {
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [newDoc, setNewDoc] = useState({ title: '', description: '', category_id: '', visibility: 'public', is_downloadable: true, is_booklet: false, allowed_client_ids: '', tags: '' });
  const fileRef = React.useRef(null);
  const replaceRef = React.useRef(null);

  const loadData = useCallback(async () => {
    try {
      const [docsRes, catsRes] = await Promise.all([
        axios.get(`${API}/admin/library/documents`, { headers }),
        axios.get(`${API}/admin/library/categories`, { headers }),
      ]);
      setDocs(docsRes.data);
      setCategories(catsRes.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const uploadDocument = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file || !newDoc.title) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', newDoc.title);
      fd.append('description', newDoc.description);
      fd.append('category_id', newDoc.category_id);
      fd.append('visibility', newDoc.visibility);
      fd.append('is_downloadable', newDoc.is_downloadable);
      fd.append('is_booklet', newDoc.is_booklet);
      fd.append('allowed_client_ids', newDoc.allowed_client_ids);
      fd.append('tags', newDoc.tags);
      await axios.post(`${API}/admin/library/documents`, fd, { headers });
      setShowUpload(false);
      setNewDoc({ title: '', description: '', category_id: '', visibility: 'public', is_downloadable: true, is_booklet: false, allowed_client_ids: '', tags: '' });
      loadData();
    } catch (err) { alert(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const replaceDocument = async (docId) => {
    const file = replaceRef.current?.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/admin/library/documents/${docId}/replace`, fd, { headers });
      loadData();
      setEditingDoc(null);
    } catch (err) { alert('Replace failed'); }
  };

  const updateDocument = async (docId, data) => {
    try {
      await axios.put(`${API}/admin/library/documents/${docId}`, data, { headers });
      loadData();
      setEditingDoc(null);
    } catch (err) { alert('Update failed'); }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/admin/library/documents/${docId}`, { headers });
      loadData();
    } catch (err) { alert('Delete failed'); }
  };

  const visibilityLabels = { public: 'Public', 'client-only': 'Activated Clients', 'specific-client': 'Specific Clients' };

  return (
    <div data-testid="library-management">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-manrope text-white/60 text-sm">{docs.length} library documents</h3>
        <button onClick={() => setShowUpload(!showUpload)} className="flex items-center gap-2 bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest px-4 py-2 rounded-sm hover:bg-[#C5A059] transition-all" data-testid="upload-library-btn">
          <Upload size={14} /> Upload Document
        </button>
      </div>

      {/* Upload Form */}
      {showUpload && (
        <div className="bg-[#111113] border border-[#D4AF37]/20 rounded-sm p-6 mb-6 space-y-4" data-testid="upload-library-form">
          <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.png,.jpg,.jpeg,.gif,.webp" className="block w-full text-white/50 font-manrope text-sm file:mr-3 file:px-4 file:py-2 file:rounded-sm file:border-0 file:bg-[#D4AF37]/20 file:text-[#D4AF37] file:font-manrope file:text-xs file:uppercase file:tracking-widest file:cursor-pointer" data-testid="library-file-input" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input value={newDoc.title} onChange={e => setNewDoc(p => ({ ...p, title: e.target.value }))} placeholder="Document Title *" className="bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white placeholder:text-white/20 font-manrope text-sm" data-testid="library-title-input" />
            <select value={newDoc.category_id} onChange={e => setNewDoc(p => ({ ...p, category_id: e.target.value }))} className="bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white font-manrope text-sm" data-testid="library-category-select">
              <option value="" className="bg-[#111]">No Category</option>
              {categories.map(c => <option key={c.id} value={c.id} className="bg-[#111]">{c.name}</option>)}
            </select>
          </div>
          <input value={newDoc.description} onChange={e => setNewDoc(p => ({ ...p, description: e.target.value }))} placeholder="Description (optional)" className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white placeholder:text-white/20 font-manrope text-sm" data-testid="library-desc-input" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <select value={newDoc.visibility} onChange={e => setNewDoc(p => ({ ...p, visibility: e.target.value }))} className="bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white font-manrope text-sm" data-testid="library-visibility-select">
              <option value="public" className="bg-[#111]">Public (all clients)</option>
              <option value="client-only" className="bg-[#111]">Activated Clients Only</option>
              <option value="specific-client" className="bg-[#111]">Specific Clients</option>
            </select>
            <label className="flex items-center gap-2 text-white/50 font-manrope text-sm cursor-pointer">
              <input type="checkbox" checked={newDoc.is_booklet} onChange={e => setNewDoc(p => ({ ...p, is_booklet: e.target.checked }))} className="accent-[#D4AF37]" /> Theater Booklet
            </label>
            <label className="flex items-center gap-2 text-white/50 font-manrope text-sm cursor-pointer">
              <input type="checkbox" checked={newDoc.is_downloadable} onChange={e => setNewDoc(p => ({ ...p, is_downloadable: e.target.checked }))} className="accent-[#D4AF37]" /> Downloadable
            </label>
          </div>
          {newDoc.visibility === 'specific-client' && (
            <select multiple value={newDoc.allowed_client_ids.split(',').filter(Boolean)} onChange={e => setNewDoc(p => ({ ...p, allowed_client_ids: Array.from(e.target.selectedOptions, o => o.value).join(',') }))} className="w-full bg-[#0f0f10] border border-white/10 rounded-sm p-2 text-white font-manrope text-sm min-h-[80px]" data-testid="library-client-select">
              {clientOptions.map(c => <option key={c.id} value={c.id} className="py-1">{c.label}</option>)}
            </select>
          )}
          <input value={newDoc.tags} onChange={e => setNewDoc(p => ({ ...p, tags: e.target.value }))} placeholder="Tags (comma separated)" className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-2 text-white placeholder:text-white/20 font-manrope text-sm" />
          <div className="flex gap-3">
            <button onClick={uploadDocument} disabled={uploading} className="bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest px-6 py-2 rounded-sm hover:bg-[#C5A059] transition-all disabled:opacity-50" data-testid="save-library-btn">{uploading ? 'Uploading...' : 'Upload'}</button>
            <button onClick={() => setShowUpload(false)} className="text-white/40 font-manrope text-xs uppercase tracking-widest px-6 py-2 border border-white/10 rounded-sm hover:border-white/30 transition-all">Cancel</button>
          </div>
        </div>
      )}

      {/* Documents List */}
      <div className="space-y-3">
        {docs.length === 0 ? (
          <div className="bg-[#111113] border border-white/5 rounded-sm p-8 text-center text-white/30 font-manrope text-sm">No library documents uploaded yet</div>
        ) : docs.map(doc => (
          <div key={doc.id} className="bg-[#111113] border border-white/5 rounded-sm p-4" data-testid={`lib-admin-${doc.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-9 h-9 rounded-sm bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                  {doc.is_booklet ? <BookOpen size={14} className="text-[#D4AF37]/60" /> : <FileText size={14} className="text-[#D4AF37]/60" />}
                </div>
                <div className="min-w-0">
                  <p className="text-white/80 font-manrope text-sm font-semibold truncate">{doc.title}</p>
                  <div className="flex items-center gap-2 text-white/30 font-manrope text-[10px]">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-bold ${doc.visibility === 'public' ? 'bg-green-500/10 text-green-400' : doc.visibility === 'client-only' ? 'bg-blue-500/10 text-blue-400' : 'bg-yellow-500/10 text-yellow-400'}`}>{visibilityLabels[doc.visibility]}</span>
                    {doc.category_name && <span>{doc.category_name}</span>}
                    <span>v{doc.version}</span>
                    <span>{doc.original_filename}</span>
                    {doc.is_booklet && <span className="text-[#D4AF37]">BOOKLET</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-3">
                <a href={`${API}/files/${doc.storage_path}`} target="_blank" rel="noreferrer" className="text-white/30 hover:text-[#D4AF37] transition-colors" title="View"><Eye size={14} /></a>
                <button onClick={() => setEditingDoc(editingDoc === doc.id ? null : doc.id)} className="text-white/30 hover:text-[#D4AF37] transition-colors" title="Edit"><Pen size={14} /></button>
                <button onClick={() => deleteDocument(doc.id)} className="text-white/30 hover:text-red-400 transition-colors" title="Delete"><Trash2 size={14} /></button>
              </div>
            </div>

            {/* Edit / Replace panel */}
            {editingDoc === doc.id && (
              <div className="mt-4 pt-4 border-t border-white/5 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-white/20 font-manrope text-[10px] uppercase tracking-widest mb-1">Title</label>
                    <input defaultValue={doc.title} id={`edit-title-${doc.id}`} className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-1 text-white font-manrope text-sm" />
                  </div>
                  <div>
                    <label className="block text-white/20 font-manrope text-[10px] uppercase tracking-widest mb-1">Visibility</label>
                    <select defaultValue={doc.visibility} id={`edit-vis-${doc.id}`} className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-1 text-white font-manrope text-sm">
                      <option value="public" className="bg-[#111]">Public</option>
                      <option value="client-only" className="bg-[#111]">Activated Clients</option>
                      <option value="specific-client" className="bg-[#111]">Specific Clients</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-white/20 font-manrope text-[10px] uppercase tracking-widest mb-1">Description</label>
                  <input defaultValue={doc.description} id={`edit-desc-${doc.id}`} className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-1 text-white font-manrope text-sm" />
                </div>
                <div className="flex gap-3">
                  <button onClick={() => {
                    updateDocument(doc.id, {
                      title: document.getElementById(`edit-title-${doc.id}`).value,
                      description: document.getElementById(`edit-desc-${doc.id}`).value,
                      visibility: document.getElementById(`edit-vis-${doc.id}`).value,
                    });
                  }} className="bg-[#D4AF37] text-black font-manrope font-bold text-[10px] uppercase tracking-widest px-4 py-1.5 rounded-sm hover:bg-[#C5A059] transition-all">Save Changes</button>
                  <label className="flex items-center gap-2 text-white/40 hover:text-[#D4AF37] font-manrope text-[10px] uppercase tracking-widest cursor-pointer px-4 py-1.5 border border-white/10 rounded-sm hover:border-[#D4AF37]/30 transition-all">
                    <input ref={replaceRef} type="file" className="hidden" onChange={() => replaceDocument(doc.id)} />
                    <RefreshCw size={12} /> Replace File
                  </label>
                </div>
                {doc.version_history?.length > 1 && (
                  <div className="mt-2">
                    <p className="text-white/20 font-manrope text-[10px] uppercase tracking-widest mb-1">Version History</p>
                    <div className="space-y-1">
                      {doc.version_history.map((v, i) => (
                        <div key={i} className="flex items-center gap-2 text-white/30 font-manrope text-[10px]">
                          <span>v{v.version}</span>
                          <span>{v.filename}</span>
                          <span>{new Date(v.uploaded_at).toLocaleDateString()}</span>
                          {v.restored_from && <span className="text-yellow-400">(restored from v{v.restored_from})</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


/* ======================== SIGNATURES TAB ======================== */
function SignaturesTab({ headers, clients }) {
  const [requests, setRequests] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [selTemplate, setSelTemplate] = useState(null);
  const [selClient, setSelClient] = useState('');
  const [fields, setFields] = useState({});
  const [signatoryName, setSignatoryName] = useState('');
  const [signatoryTitle, setSignatoryTitle] = useState('');
  const [viewing, setViewing] = useState(null);
  const [sending, setSending] = useState(false);
  const adminToken = localStorage.getItem('admin_token');

  const load = useCallback(async () => {
    try {
      const [reqRes, tplRes] = await Promise.all([
        axios.get(`${API}/admin/signature-requests`, { headers }),
        axios.get(`${API}/admin/signature-templates`, { headers }),
      ]);
      setRequests(reqRes.data);
      setTemplates(tplRes.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!selTemplate || !selClient || !signatoryName.trim()) { alert('Select a template, client, and enter the signatory name'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/admin/signature-requests`, {
        template_key: selTemplate.key, client_id: selClient,
        admin_fields: fields, signatory_name: signatoryName.trim(), signatory_title: signatoryTitle.trim(),
      }, { headers });
      alert('Signature request sent — the client has been notified by email.');
      setShowNew(false); setSelTemplate(null); setSelClient(''); setFields({}); setSignatoryName(''); setSignatoryTitle('');
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };

  const removeRequest = async (id) => {
    if (!window.confirm('Delete this signature request?')) return;
    try { await axios.delete(`${API}/admin/signature-requests/${id}`, { headers }); load(); }
    catch (err) { alert('Delete failed'); }
  };

  const openView = async (id) => {
    try { const res = await axios.get(`${API}/admin/signature-requests/${id}`, { headers }); setViewing(res.data); }
    catch (e) { /* ignored */ }
  };

  const inputCls = "w-full bg-[#111113] border border-white/10 text-white font-manrope text-sm rounded-sm px-3 py-2 focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20";

  return (
    <div data-testid="signatures-management">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-manrope text-white/60 text-sm">{requests.length} signature request{requests.length !== 1 ? 's' : ''}</h3>
        <button onClick={() => setShowNew(!showNew)} className="flex items-center gap-2 bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest px-4 py-2 rounded-sm hover:bg-[#C5A059] transition-all" data-testid="new-signature-request-btn">
          <Plus size={14} /> New Signature Request
        </button>
      </div>

      {showNew && (
        <div className="mb-8 bg-[#111113] border border-[#D4AF37]/20 rounded-sm p-6" data-testid="new-signature-form">
          <h4 className="font-manrope text-[#D4AF37] text-xs uppercase tracking-widest font-bold mb-4">1. Choose Document</h4>
          <div className="grid md:grid-cols-2 gap-3 mb-6">
            {templates.map(t => (
              <button key={t.key} onClick={() => { setSelTemplate(t); setFields({}); }} className={`text-left p-4 rounded-sm border transition-all ${selTemplate?.key === t.key ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/10 hover:border-white/25'}`} data-testid={`template-${t.key}`}>
                <p className="font-manrope text-white text-sm font-bold mb-1">{t.title}</p>
                <p className="font-manrope text-white/40 text-xs">{t.description}</p>
              </button>
            ))}
          </div>

          {selTemplate && (
            <>
              <h4 className="font-manrope text-[#D4AF37] text-xs uppercase tracking-widest font-bold mb-3">2. Select Client</h4>
              <select value={selClient} onChange={e => setSelClient(e.target.value)} className={`${inputCls} mb-6`} data-testid="signature-client-select">
                <option value="">— Choose a client —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
              </select>

              {selTemplate.admin_fields.length > 0 && (
                <>
                  <h4 className="font-manrope text-[#D4AF37] text-xs uppercase tracking-widest font-bold mb-3">3. Fill Document Details</h4>
                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    {selTemplate.admin_fields.map(f => (
                      <div key={f.key} className={f.type === 'multiline' ? 'md:col-span-2' : ''}>
                        <label className="text-[10px] uppercase tracking-widest text-white/40 font-manrope block mb-1">{f.label}</label>
                        {f.type === 'multiline' ? (
                          <textarea rows={2} value={fields[f.key] || ''} onChange={e => setFields(prev => ({ ...prev, [f.key]: e.target.value }))} className={inputCls} data-testid={`field-${f.key}`} />
                        ) : (
                          <input type={f.type === 'date' ? 'date' : 'text'} value={fields[f.key] || ''} onChange={e => setFields(prev => ({ ...prev, [f.key]: e.target.value }))} className={inputCls} data-testid={`field-${f.key}`} />
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              <h4 className="font-manrope text-[#D4AF37] text-xs uppercase tracking-widest font-bold mb-3">4. Company Signatory</h4>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <input type="text" value={signatoryName} onChange={e => setSignatoryName(e.target.value)} placeholder="Authorized signatory name" className={inputCls} data-testid="signatory-name-input" />
                <input type="text" value={signatoryTitle} onChange={e => setSignatoryTitle(e.target.value)} placeholder="Title (e.g. Owner, Project Manager)" className={inputCls} data-testid="signatory-title-input" />
              </div>
              {signatoryName.trim() && (
                <p className="mb-6 font-manrope text-xs text-white/40">Will be signed as: <span className="text-[#D4AF37] italic text-lg" style={{ fontFamily: 'cursive' }}>{signatoryName}</span></p>
              )}

              <button onClick={send} disabled={sending} className="flex items-center gap-2 bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest px-6 py-3 rounded-sm hover:bg-[#C5A059] transition-all disabled:opacity-40" data-testid="send-signature-request-btn">
                <Send size={14} /> {sending ? 'Sending...' : 'Send to Client for Signature'}
              </button>
            </>
          )}
        </div>
      )}

      {requests.length === 0 ? (
        <div className="bg-[#111113] border border-white/5 rounded-sm p-8 text-center text-white/30 font-manrope text-sm">No signature requests yet. Create one to send a contract or release form to a client.</div>
      ) : (
        <div className="space-y-2">
          {requests.map(r => (
            <div key={r.id} className="bg-[#111113] border border-white/5 rounded-sm p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`sig-request-${r.id}`}>
              <div>
                <p className="font-manrope text-white text-sm font-bold">{r.title}</p>
                <p className="font-manrope text-white/40 text-xs">{r.client_name} ({r.client_email}) • Sent {r.created_at?.slice(0, 10)}</p>
              </div>
              <div className="flex items-center gap-2">
                {r.status === 'signed' ? (
                  <span className="flex items-center gap-1 text-green-400 font-manrope text-[10px] uppercase tracking-widest font-bold"><Check size={12} /> Signed {r.signed_at?.slice(0, 10)}</span>
                ) : (
                  <span className="text-amber-400/80 font-manrope text-[10px] uppercase tracking-widest font-bold">Awaiting Signature</span>
                )}
                <button onClick={() => openView(r.id)} className="text-white/40 hover:text-[#D4AF37] border border-white/10 rounded-sm p-2 transition-colors" title="View" data-testid={`view-sig-${r.id}`}><Eye size={14} /></button>
                <a href={`${API}/signature-requests/${r.id}/pdf?token=${adminToken}`} target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-[#D4AF37] border border-white/10 rounded-sm p-2 transition-colors" title="Download PDF" data-testid={`pdf-sig-${r.id}`}><Download size={14} /></a>
                <button onClick={() => removeRequest(r.id)} className="text-white/40 hover:text-red-400 border border-white/10 rounded-sm p-2 transition-colors" title="Delete"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {viewing && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={() => setViewing(null)}>
          <div className="bg-[#111113] border border-[#D4AF37]/20 rounded-sm w-full max-w-2xl max-h-[85vh] overflow-y-auto p-8" onClick={e => e.stopPropagation()} data-testid="view-signature-modal">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="font-playfair text-white text-xl">{viewing.title}</h3>
                <p className="font-manrope text-white/40 text-xs mt-1">{viewing.client_name} • {viewing.status === 'signed' ? `Signed ${viewing.signed_at?.slice(0, 10)}` : 'Awaiting signature'}</p>
              </div>
              <button onClick={() => setViewing(null)} className="text-white/40 hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-4 mb-6">
              {(viewing.rendered_sections || []).map((s, i) => (
                <div key={i}>
                  {s.heading && <h4 className="font-manrope text-white text-sm font-bold mb-1">{s.heading}</h4>}
                  {s.body.map((p, j) => <p key={j} className="font-manrope text-white/40 text-xs leading-relaxed mb-1">{p}</p>)}
                  {s.bullets.map((b, j) => <p key={j} className="font-manrope text-white/40 text-xs leading-relaxed pl-4 mb-1">• {b}</p>)}
                  {s.after && <p className="font-manrope text-white/40 text-xs leading-relaxed">{s.after}</p>}
                </div>
              ))}
            </div>
            {viewing.status === 'signed' && viewing.client_signature?.type === 'drawn' && viewing.client_signature?.data && (
              <div className="border-t border-white/10 pt-4">
                <p className="font-manrope text-[#D4AF37] text-[10px] uppercase tracking-widest font-bold mb-2">Client Signature</p>
                <img src={viewing.client_signature.data} alt="Client signature" className="bg-white/90 rounded-sm max-h-24" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

